import { useQuery } from '@tanstack/react-query'
import type { Encounter } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import { useOrpc } from './useClient'

export type ReturningItem = { encounterId: string; patientId: string; before: string }

/**
 * Pure: extract the (encounterId, patientId, before) tuples the middleware needs to
 * decide which patients are Returning. `before` is the start of the appointment's
 * calendar day in the viewer's local timezone, so the "earlier calendar day" rule
 * matches what the calendar renders. Encounters missing any field are skipped.
 */
export function buildReturningItems(encounters: Encounter[]): ReturningItem[] {
  const items: ReturningItem[] = []
  for (const encounter of encounters) {
    const encounterId = encounter.id
    const patientReference = encounter.subject?.reference
    const start = encounter.period?.start
    if (!encounterId || !patientReference?.startsWith('Patient/') || !start) {
      continue
    }
    items.push({
      encounterId,
      patientId: patientReference.split('/')[1],
      before: dayjs(start).startOf('day').toISOString(),
    })
  }
  return items
}

/**
 * Maps each visible encounter id to whether its patient is "Returning": i.e. had at
 * least one Completed (`fulfilled`) appointment or finished clinical Encounter on an
 * earlier calendar day than this appointment. The query runs in the middleware
 * (`getReturningPatientStatuses`); the
 * query key and fetcher are generated by the oRPC TanStack Query utils.
 */
export function useReturningPatientMap(encounters: Encounter[]) {
  const orpc = useOrpc()

  // Sort for a stable query key regardless of encounter ordering.
  const items = buildReturningItems(encounters).sort((a, b) =>
    a.encounterId.localeCompare(b.encounterId),
  )

  const query = useQuery(
    orpc.getReturningPatientStatuses.queryOptions({
      input: { items },
      enabled: items.length > 0,
      staleTime: 5 * 60 * 1000,
      select: ({ returningEncounterIds }): Record<string, boolean> => {
        const map: Record<string, boolean> = {}
        for (const encounterId of returningEncounterIds) {
          map[encounterId] = true
        }
        return map
      },
    }),
  )

  return items.length === 0 ? { ...query, data: {} } : query
}
